import type { MarkupPreviewPost } from "../../../../components/Collaboration/markup/markup-model";
/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "../../../../styles/color-tokens";
import { useDispatch, useSelector } from "react-redux";
import {
  checkInstagramPostOverlapping,
  checkInstagramPreflight,
  updatePostFormField,
} from "../../../../actions/postForm";
import { getPage, getUser } from "../../../../selectors/commonSelectors";

import {
  getOriginalPost,
  type PostFormRootState,
} from "../../../../selectors/postFormSelectors";

import StatusChange from "../../../../components/Collaboration/StatusChange/StatusChange";
import { FIELDS } from "../../../../constants/PostFormFields";
import { PostFormActions } from "../../../../components/Posts/PostForm";
import PlatformTabs from "../../../../components/Posts/PostForm/PlatformTabs/PlatformTabs";
import PostFieldError from "../../../../components/Posts/PostForm/PostFieldError/PostFieldError";
import { generatePostLinkToPostObject } from "../../../../components/Posts/Post/PostLinkToPost/PostLinkToPost";
import NewCollaborationChat from "../../../../components/Collaboration/CollaborationChat/CollaborationChat";
import { resolveCollaborationChannelId } from "../../../../components/Collaboration/CollaborationChat/resolve-collaboration-channel-id";
import { PostSelectedAssets } from "../../../../features/post/components/post-selected-assets/post-selected-assets";
import { CollaborationIsNotEnabled } from "../../../../components/Collaboration/CollaborationIsNotEnabled";
import {
  filterAccountsByPlatformType,
  findManualPlatform,
  getStoryAccountLabel,
} from "../../../../utils/accounts";
import { PostFormBaseTemplate } from "./post-form-base-template";
import { Alert, AlertType } from "../../../../ui/alert";
import { useTrackEvent } from "../../../../libs/analytics";
import { AccountType } from "../../../../shared/types/accounts";
import { POST_STATES } from "../../../../constants/PostsView";
import {
  COPY_TO_DRAFT_ACTION,
  NEW_CAL_POST_AGAIN_ACTION,
} from "../../../../constants/Events";
import { ONLY_MANUAL_PLATFORMS } from "../../../../constants/Platforms";
import styled from "styled-components";
import ErrorBoundary from "../../../../components/ErrorBoundary/ErrorBoundary";
import type { Label } from "./post-form-base-template";
import { useContentLabelSection } from "./content-label-section";
import { hasTeamwork } from "../../../../utils/feature-gates";
import { hasAuthoritativeBasicPostStatuses } from "../../../../utils/post-statuses";
import { isNewPostId, getPostPreviewModel } from "../../../../utils/posts";
import {
  BulkTabContext,
  FormContextValue,
  SaveAction,
} from "../../../../components/Posts/PostForm/types";
import { Button } from "../../../../components/ui/button";
import { Notification } from "../../../../components/ui/notification";
import { PostSaveButton } from "../post-save-button";
import { queryKeys } from "../../../../api/query-keys";
import { SkedPostPreview } from "../../../../components/Posts/SkedPostPreview";
import config from "../../../../config";
import {
  PostMarkupProvider,
  useOptionalPostMarkup,
} from "../../../../components/Collaboration/markup/PostMarkupProvider";

const commentsEmptyStateImageUrl = `${config.resourceUrl}img/comments-empty-state.png`;
const quirkyGlassesImageUrl = `${config.resourceUrl}img/quirky/quirky-glasses.svg`;

interface Props {
  formTitle?: string;
  renderAccountsSelector: () => React.ReactNode;
  children?: JSX.Element;
  isContentLabelFeatureEnabled: boolean;
  contentLabelOptions: Label[];
  selectedContentLabel: Label[];
  isFetchingAccountGroups: boolean;
  bulkTab: BulkTabContext | undefined;
}

export const PostFormContext = React.createContext<FormContextValue | null>(
  null
);

export function PostFormTemplate(props: Props) {
  const form = useContext(PostFormContext);
  const user = useSelector(getUser);
  const formId = props.bulkTab?.formId ?? form?.post._id;
  const originalPost =
    useSelector((state: PostFormRootState) =>
      formId ? getOriginalPost(state, formId) : undefined
    ) ?? form?.post;
  const savedPostModel = useMemo(
    () =>
      originalPost && form?.previewAccounts?.length
        ? getPostPreviewModel(
            originalPost,
            form.previewAccounts,
            form.selectedPlatform,
            form.previewAccountId
          )
        : null,
    [
      originalPost,
      form?.previewAccounts,
      form?.selectedPlatform,
      form?.previewAccountId,
    ]
  );
  if (!form)
    throw new Error("PostFormTemplate must be used within PostFormContext");
  return (
    <PostMarkupProvider
      postId={form.post._id}
      channelId={form.channelId}
      postState={form.postState}
      user={user}
      post={form.postModel as MarkupPreviewPost | null}
      savedPost={savedPostModel as MarkupPreviewPost | null}
      isNewPost={Boolean(form.post.newPost) || isNewPostId(form.post._id)}
      disabled={form.isSummary || form.allDisabled}
      initialTab={
        !form.post.newPost &&
        hasTeamwork(user) &&
        !form.post.isSuccess &&
        !form.post.past &&
        form.post.postType !== "archived"
          ? "activity"
          : "preview"
      }
    >
      <PostFormTemplateContent {...props} />
    </PostMarkupProvider>
  );
}

function PostFormTemplateContent({ ...props }: Props) {
  const formContext = useContext(PostFormContext);
  const review = useOptionalPostMarkup();

  if (!formContext) {
    throw new Error("PostFormTemplate must be used within PostFormContext");
  }

  const {
    allDisabled,
    mediaControlsDisabled,
    updateField,
    updateFields,
    selectedAccountIds,
    postFormValue,
    platforms,
    selectPlatform,
    errors,
    warnings,
    post,
    postModel,
    channelId,
    postState,
    platformPost,
    originalPostState,
    onCancel,
    onSubmit,
    saveDisabled,
    isSubmitting,
    isSummary,
  } = formContext;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector(getUser);

  const trackEvent = useTrackEvent();
  const page = useSelector(getPage);

  const collaborationAvailable = hasTeamwork(user);
  // When the user has no configured post statuses (e.g. a plan without
  // approvals), StatusChange has nothing to populate the picker with. Offer a
  // single selectable "Ready to publish" pseudo-status so the control isn't an
  // empty dropdown and the post can still be created in a publishable state
  // (SKE-3735 AC C4). Users WITH statuses (incl. downgraded users whose
  // statuses are preserved) rely on StatusChange's own option handling.
  const hasNoPostStatuses =
    !user?.postStatuses?.length && !hasAuthoritativeBasicPostStatuses(user);
  const instagramAccounts: AccountType[] = useMemo(
    () =>
      postFormValue?.accounts?.filter(filterAccountsByPlatformType("IG")) || [],
    [postFormValue?.accounts]
  );

  let canQueuePosts = true;
  if (
    postFormValue &&
    postFormValue.accounts &&
    postFormValue.accounts.length > 0
  ) {
    canQueuePosts = postFormValue.accounts.every(function (account) {
      return !!account.queueEnabled;
    });
  }

  useEffect(() => {
    dispatch(
      checkInstagramPostOverlapping({
        postId: postFormValue._id,
        accounts: postFormValue.accounts,
        isNow: postFormValue.isNow,
        when: postFormValue.when,
        post: postFormValue,
      })
    );
  }, [postFormValue.isNow, postFormValue.when, instagramAccounts?.length]);

  /*
   * How this draft would publish, per account. SKE-4567.
   *
   * Re-run on the things that can change the answer: which accounts are
   * selected, the media count, because the story-chain limit is the whole
   * reason the composer asks, and the location, which the server checks still
   * exists. Not on every keystroke: the caption cannot change the track.
   */
  useEffect(() => {
    dispatch(
      checkInstagramPreflight({
        postId: postFormValue._id,
        accounts: postFormValue.accounts,
        post: postFormValue,
      })
    );
  }, [
    instagramAccounts?.length,
    postFormValue.mediaFiles?.length,
    postFormValue.type,
    postFormValue.storyType,
    postFormValue.location,
  ]);

  const { renderLabels } = useContentLabelSection({
    ...props,
    isSummary,
    postFormValue,
  });

  const { url: postLinkToPostUrl, platformLabel: postLinkToPostPlatformLabel } =
    generatePostLinkToPostObject(post, postFormValue.accounts);

  const onStatusChange = async (newPostStatusKey: string) => {
    await dispatch(
      updatePostFormField(postFormValue._id, {
        [FIELDS.POST_STATUS]: newPostStatusKey,
      })
    );
    if (newPostStatusKey === postFormValue.postStatusKey) {
      return;
    }
    if (collaborationAvailable) {
      const channelId =
        resolveCollaborationChannelId(platformPost || postFormValue) ?? "";
      // Server require some time to update the post status and only then we can get updated info in the new request
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.collaboration.comments(channelId),
        });
      }, 1000);
    }
  };

  const handlePostSave = (saveAction: SaveAction | undefined) => {
    if (allDisabled || saveDisabled || isSubmitting) {
      return;
    }

    //if archived post posted again from calendar page
    if (post.originalPostState) {
      trackEvent({ eventName: NEW_CAL_POST_AGAIN_ACTION });
    }

    //if archived post copied to drafts from archive page
    if (
      !post.failedAt &&
      (!post.originalPostState || originalPostState !== "DRAFT") &&
      !isNewPostId(post._id) &&
      postState === "DRAFT"
    ) {
      trackEvent({
        eventName: COPY_TO_DRAFT_ACTION,
        metadata: { postSavedAs: postState },
      });
    }

    if (review?.enabled) review.requestLeave(() => onSubmit(saveAction));
    else onSubmit(saveAction);
  };

  if (props.bulkTab?.childCallbacks) {
    props.bulkTab.childCallbacks.onSubmit = (
      saveAction: SaveAction | undefined
    ) => {
      handlePostSave(saveAction);
    };
  }

  const handleTrackEvent = (eventName: string) => {
    if (page) {
      trackEvent({ eventName, metadata: { page: page.toLocaleLowerCase() } });
    }
  };

  return (
    <PostFormBaseTemplate
      formTitle={props.formTitle}
      isSubmitting={isSubmitting}
      isNewPost={Boolean(post.newPost)}
      isPostFailedOrPosted={Boolean(
        (post.postType === "archived" && !post.isSuccess) ||
        post.isSuccess ||
        post.past
      )}
      isCollaborationAvailable={Boolean(collaborationAvailable)}
      renderAccountsSelector={props.renderAccountsSelector}
      renderMediaSelector={() => (
        <PostSelectedAssets
          post={postFormValue}
          className="flex flex-col"
          onChange={(mediaFiles) => updateField(FIELDS.MEDIA, mediaFiles)}
          errors={errors}
          warnings={warnings}
          disabled={mediaControlsDisabled}
          selectedAccountIds={selectedAccountIds}
        />
      )}
      renderContentLabel={renderLabels}
      renderFields={() =>
        selectedAccountIds.length > 0 && (
          <div
            className="ml-[48px] flex min-w-0 flex-grow rounded-r-lg rounded-bl-lg bg-white"
            data-testid="post-platform-fields"
          >
            <div
              className="relative z-20 -ml-[48px] flex w-[48px] flex-none self-stretch flex-col overflow-hidden rounded-l-lg bg-[#f4f4f6]"
              data-testid="post-platform-tabs-container"
            >
              <PlatformTabs
                platforms={platforms}
                onChange={(platform: Parameters<typeof selectPlatform>[0]) =>
                  review?.enabled
                    ? review.requestLeave(() => selectPlatform(platform))
                    : selectPlatform(platform)
                }
              />
            </div>
            <div className="min-w-0 flex-1 p-4">{props.children}</div>
          </div>
        )
      }
      renderFieldMessages={() => (
        <>
          {isSummary ? (
            <div className="mt-4">
              <Alert type={AlertType.success}>
                Successfully Published.{" "}
                {postLinkToPostUrl && postLinkToPostPlatformLabel && (
                  <a
                    href={postLinkToPostUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View {postLinkToPostPlatformLabel}.
                  </a>
                )}
              </Alert>
            </div>
          ) : (
            <PostFieldError
              className="mt-4"
              fields={[FIELDS.POST_STATE, "SAVE_ERROR"]}
              errors={errors}
              // SKE-4733. Carries the non-blocking overlap-check warning.
              warnings={warnings}
            />
          )}

          {postState === POST_STATES.ARCHIVED &&
            post.failReason &&
            Object.keys(errors).length === 0 && (
              <Notification variant="error" title={post.failReason} />
            )}
        </>
      )}
      renderPreview={() => (
        <PostPreviewTab
          previewSurface={props.bulkTab ? "bulk_create" : "create_post"}
          platformPost={platformPost}
          postModel={postModel as any}
          postFormValue={postFormValue}
          trackEvent={handleTrackEvent}
        />
      )}
      renderCollaboration={() =>
        collaborationAvailable ? (
          post.newPost ? (
            <EmptyState>
              <img
                src={commentsEmptyStateImageUrl}
                alt="empty-comment"
                width="100%"
              />
              <p>Add comments after creating</p>
              <span>
                You can add comments to your post or draft after it's created.
              </span>
            </EmptyState>
          ) : (
            <NewCollaborationChat
              channelId={channelId}
              postState={postState}
              postId={post._id}
              user={user}
              disabled={isSummary}
            />
          )
        ) : (
          <CollaborationIsNotEnabled />
        )
      }
      renderActions={() => (
        <>
          {/* Discard */}
          <Button
            size="sm"
            variant="black-secondary"
            className="w-[71px] rounded-lg text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
            onClick={onCancel}
          >
            Discard
          </Button>

          {/* All other actions */}
          <div className="flex-grow flex items-center justify-end gap-2">
            <div data-testid="postCreateStatusChange">
              <StatusChange
                post={platformPost}
                disabled={allDisabled}
                onChange={onStatusChange}
                originalPostState={originalPostState}
                selectedStatusKey={
                  hasNoPostStatuses ? "readytopublish" : undefined
                }
                additionalOptions={
                  hasNoPostStatuses
                    ? [
                        {
                          label: "Ready to publish",
                          value: "readytopublish",
                          color: colors.success500,
                        },
                      ]
                    : undefined
                }
                triggerClassName="h-8 w-[240px] min-w-[240px] px-3 py-1.5 font-medium"
              />
            </div>
            {/* Action */}
            <PostFormActions
              post={postFormValue}
              postState={postState}
              samePostState={originalPostState === postState}
              timePreference={user?.preferences?.timePreference ?? "24h"}
              onChange={updateFields}
              disabled={allDisabled}
              canQueuePosts={canQueuePosts}
              isSummary={isSummary}
              inBulkTab={!!props.bulkTab}
            />

            {isSummary ? (
              <Button variant="blue-primary" size="md" onClick={onCancel}>
                Close
              </Button>
            ) : props.bulkTab ? (
              <PostSaveButton
                disabled={allDisabled || saveDisabled}
                onSave={handlePostSave}
                postState={postState}
                isPostNow={!!post.isNow}
                isSubmitting={isSubmitting}
                label={props.bulkTab.isEditing ? "Save" : undefined}
                compact
              />
            ) : (
              <Button
                data-testid="postSaveButton"
                variant="blue-primary"
                size="md"
                disabled={allDisabled || saveDisabled || isSubmitting}
                onClick={() => handlePostSave(undefined)}
                title={
                  Object.keys(errors)?.length > 0
                    ? "Post cannot be saved as you still have some errors to fix!"
                    : ""
                }
              >
                Save
              </Button>
            )}
          </div>
        </>
      )}
    />
  );
}

const PostPreviewTab = ({
  previewSurface,
  postModel,
  postFormValue,
  platformPost,
  trackEvent,
}: {
  previewSurface: string;
  postModel: { platform?: string } | null | undefined;
  postFormValue: FormContextValue["postFormValue"];
  platformPost: FormContextValue["platformPost"];
  trackEvent: (eventName: string) => void;
}) => {
  const hasManualTTPlatform = findManualPlatform(postFormValue.accounts, "TT");
  const hasManualTHPlatform = findManualPlatform(postFormValue.accounts, "TH");
  const manualPublishingLabel = getStoryAccountLabel(
    postFormValue[FIELDS.STORY_TYPE] ?? "story",
    postFormValue.accounts,
    postModel?.platform ?? "IG"
  );
  const { videoCoverPreview } = platformPost;
  const preferVideoCoverPreviewOverride =
    platformPost.videoCoverType === "frame_selected" &&
    Boolean(videoCoverPreview) &&
    videoCoverPreview !== platformPost.thumbnailUrl;

  return (
    <ErrorBoundary>
      {postModel && (
        <div data-testid="skedPreview">
          <SkedPostPreview
            previewSurface={previewSurface}
            videoCoverPreview={videoCoverPreview ?? undefined}
            preferVideoCoverPreviewOverride={preferVideoCoverPreviewOverride}
            post={postModel as never}
            trackEvent={trackEvent}
            env="schedugram-client"
          />
        </div>
      )}
      {(postFormValue[FIELDS.STORY_TYPE] &&
        postFormValue[FIELDS.STORY_TYPE] !== "story") ||
        ((hasManualTTPlatform ||
          hasManualTHPlatform ||
          (postModel?.platform !== undefined &&
            ONLY_MANUAL_PLATFORMS.includes(postModel.platform as never))) && (
          <Notification
            className="mt-4 w-full"
            variant="info"
            title="Manual publishing required"
            description={`We don't support ${manualPublishingLabel} auto-posting yet. This post will appear in Upcoming Posts, and you'll be notified when it's time to publish it manually.`}
          />
        ))}
      {!postModel && (
        <div className=" flex items-center justify-end h-300px text-lg text-blue-500">
          <img
            src={quirkyGlassesImageUrl}
            alt="quirky"
            className="max-w-full max-h-full"
          />
        </div>
      )}
    </ErrorBoundary>
  );
};

const EmptyState = styled.div`
  width: 280px;
  margin: 60px auto 0;

  img {
    margin-bottom: 12px;
  }

  span,
  p {
    font-size: 14px;
    line-height: 17px;
    color: #000000;
    display: block;
    margin-left: 10px;
  }

  p {
    font-weight: 500;
    margin-bottom: 12px;
  }

  span {
    font-weight: 300;
  }
`;
